import React, { useState, useContext, useRef, useEffect } from "react";
import emptyImage from "../images/empty.jpg";
import ApiService from "../api/apiService";
import showSwal from "./swal";
import { common } from "../util/common";
import autosize from "autosize";
import loader2 from "../images/loader_2.gif";
import { ChatContext, WebSocketContext } from "../context/contexts";
import { convertTo12HourFormat, getTimeAgo, formatBoldText } from "../util/helperFunctions";
import MessageComponent from "./message";
import ReactionsIndicator from "./reusable/reactions-indicator";
import { groupReactions } from "../util/helperFunctions";
import { resizeFile } from "../hooks/resizeImage";
import { useMentionComposer } from "../hooks/useMentionComposer";
import { MentionComposerField, MentionPicker } from "./mention-picker";
import "./view-thread-modal.css";

function ViewThreadModal({ userId, handleImageClick, allowAiCommand = false }) {
  const Chat = useContext(ChatContext);
  const WS = useContext(WebSocketContext);
  const textAreaRef = useRef(null);
  const fileInputRef = useRef(null);

  const { user, content, createdAt, id, is_deleted, message_attachment, thread_replies, message_reactions } =
    Chat.threadViewDetails;

  const [selectedFile, setSelectedFile] = useState([]);
  const [filePreview, setFilePreview] = useState([]);

  const [messageContent, setMessageContent] = useState("");
  const [isSendingMsg, setIsSendingMsg] = useState(false);
  const [selectedMentionData, setSelectedMentionData] = useState([]);
  const mention = useMentionComposer({
    messageContent,
    setMessageContent,
    setSelectedMentionData,
    textareaRef: textAreaRef,
    members: Chat.conversationMembersData,
    includeAiCommand: allowAiCommand,
    onlineUsers: Chat.onlineUsers,
  });

  const [onlineUserData, setOnlineUserData] = useState(null);
  const [isPostExpanded, setIsPostExpanded] = useState(false);
  const [isPostTruncatable, setIsPostTruncatable] = useState(false);
  const messageBodyRef = useRef(null);

  useEffect(() => {
    const onlineUsers = Chat.onlineUsers;

    const onlineData = onlineUsers.find((onlineUser) => onlineUser.user_id === user?.id);
    setOnlineUserData(onlineData);
  }, [Chat.onlineUsers, user?.id]);

  useEffect(() => {
    if (!textAreaRef.current) return;
    const textareaEl = textAreaRef.current;
    autosize(textareaEl);
    return () => autosize.destroy(textareaEl);
  }, []);

  useEffect(() => {
    setIsPostExpanded(false);
    setIsPostTruncatable(false);
  }, [id, content]);

  useEffect(() => {
    if (is_deleted || isPostExpanded) return;

    const messageBodyEl = messageBodyRef.current;
    if (!messageBodyEl) return;

    const frameId = requestAnimationFrame(() => {
      setIsPostTruncatable(messageBodyEl.scrollHeight > messageBodyEl.clientHeight + 2);
    });

    return () => cancelAnimationFrame(frameId);
  }, [content, is_deleted, isPostExpanded]);

  const allItems = [...thread_replies].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

  const handleFileChange = (event) => {
    const files = event.target.files;
    const maxFileSizeMB = process.env.REACT_APP_MAX_ATTACHMENT_SIZE_IN_MB;
    const newFilePreviews = [];
    const newSelectedFiles = [];

    if (files.length > 0) {
      for (let i = 0; i < files.length; i++) {
        const fileSizeMB = files[i].size / 1024 / 1024;
        if (fileSizeMB > maxFileSizeMB) {
          showSwal({
            title: "Something went wrong",
            icon: "error",
            text: `File ${files[i].name} exceeds 15MB and will not be uploaded.`,
            confirmButtonText: "OK",
          });
          continue;
        }
        const objProperty = {
          filePreview: URL.createObjectURL(files[i]),
          file: files[i],
          type: files[i].type,
        };
        newSelectedFiles.push(objProperty);
        newFilePreviews.push(objProperty);
      }
      setSelectedFile(newSelectedFiles);
      setFilePreview(newFilePreviews);
    }
  };

  const handleRemoveFile = (filePreviewUrl) => {
    setFilePreview(filePreview.filter((file) => file.filePreview !== filePreviewUrl));
    setSelectedFile(selectedFile.filter((file) => file.filePreview !== filePreviewUrl));
  };

  const handleMessageInput = (event) => {
    mention.handleChange(event);
  };

  const renderAttachment = (
    attachment,
    file_type,
    thread_attachment,
    index,
    lightboxImages = null,
    lightboxIndex = 0,
  ) => {
    const FINAL_RESOURCES_URL = process.env.REACT_APP_CHAT_RESOURCES_URL;

    const attachmentLink = `${FINAL_RESOURCES_URL}/${attachment}`;

    const img = (
      <img
        src={attachmentLink}
        alt='attachment'
        onClick={() =>
          handleImageClick(
            lightboxImages?.length
              ? lightboxImages
              : [{ src: attachmentLink, type: "image" }],
            lightboxIndex,
          )
        }
        className='thread-image-attachment'
        key={index}
      />
    );

    const video = (
      <video
        controls
        src={attachmentLink}
        className='thread-image-attachment'
        // onClick={() => handleImageClick([{ type: "video", sources: [{ src: attachmentLink, type: "video/mp4" }] }])}
        style={{ cursor: "pointer" }}
        key={index}
      >
        Your browser does not support the video tag.
      </video>
    );
    const pdf = (
      <a href={attachmentLink} target='_blank' rel='noopener noreferrer' key={index}>
        <div className='attachment-column'>
          <i className='fa fa-file-o fa-5x' aria-hidden='true'></i>
          <small>{attachment}</small>
        </div>
      </a>
    );

    const _pdf = (
      <a
        href={attachmentLink}
        className='message__item-attachment-file view-thread-reply-attachment'
        target='_blank'
        rel='noopener noreferrer'
        key={index}
      >
        Click to view the file
      </a>
    );

    if (file_type === "application/pdf") {
      if (thread_attachment) {
        return _pdf;
      } else {
        return pdf;
      }
    } else if (file_type.includes("video")) {
      return video;
    } else if (file_type.includes("image")) {
      return img;
    } else {
      if (thread_attachment) {
        return _pdf;
      } else {
        return pdf;
      }
    }
  };

  const buildProfileImageSrc = (userProfile) => {
    const profileImage =
      userProfile === "" || userProfile === null
        ? emptyImage
        : `${process.env.REACT_APP_IMAGES_DATA_RESOURCES_URL}/${userProfile}`;

    return profileImage;
  };

  const renderUserChatAttachment = (attachment, index) => {
    const img = (
      <div className='attachment' key={index}>
        <span onClick={() => handleRemoveFile(attachment.filePreview)}>X</span>
        <img
          src={attachment.filePreview}
          alt=''
          onClick={(e) => handleImageClick([{ src: attachment.filePreview, type: "image" }])}
          className='thread-image-attachment'
        />
      </div>
    );

    const video = (
      <div className='attachment' key={index}>
        <span onClick={() => handleRemoveFile(attachment.filePreview)}>X</span>
        <video
          src={attachment.filePreview}
          className='thread-image-attachment'
          style={{ cursor: "pointer" }}
        ></video>
      </div>
    );

    const pdf = (
      <div className='attachment' key={index}>
        <span onClick={() => handleRemoveFile(attachment.filePreview)}>X</span>
        <div className='attachment-column'>
          <i className='fa fa-file-o fa-5x' aria-hidden='true'></i>
          <small>{attachment.file.name}</small>
        </div>
      </div>
    );

    if (attachment.type.includes("image")) {
      return img;
    } else if (attachment.type.includes("vide")) {
      return video;
    } else {
      return pdf;
    }
  };

  const handleMessageInputPaste = (event) => {
    const clipboardItems = event.clipboardData.items;
    const dataTransfer = new DataTransfer();

    if (fileInputRef.current && fileInputRef.current.files.length > 0) {
      for (let i = 0; i < fileInputRef.current.files.length; i++) {
        dataTransfer.items.add(fileInputRef.current.files[i]);
      }
    }

    for (let i = 0; i < clipboardItems.length; i++) {
      const item = clipboardItems[i];

      if (item.kind === "file") {
        const file = item.getAsFile();
        if (file) {
          dataTransfer.items.add(file);
        }
      }
    }

    if (dataTransfer.files.length > 0) {
      fileInputRef.current.files = dataTransfer.files;

      handleFileChange({
        target: {
          files: dataTransfer.files,
        },
      });
    }
  };

  const handleRemoveFiles = () => {
    setSelectedFile([]);
    setFilePreview([]);
    if (fileInputRef) fileInputRef.current.value = "";
  };

  const handleSendMessage = async () => {
    if (isSendingMsg) return;
    if (!Chat.storedConversationId) return showSwal(common.errors.INVALID_CONVERSATION);
    if (!messageContent) return showSwal(common.errors.EMPTY_MESSAGE);

    try {
      const formData = new FormData();
      if (selectedFile.length > 0) {
        for (const fileObj of selectedFile) {
          const resizedFile = await resizeFile(fileObj.file, 800, 800); // Set max dimensions
          formData.append("message_attachments", resizedFile, fileObj.file.name);
        }
      }
      formData.append(
        "data",
        JSON.stringify({
          message: messageContent,
          mention_data: selectedMentionData,
        })
      );
      setIsSendingMsg(true);

      const replyDetail = await ApiService.message.replyThreadMessage(formData, Chat.storedConversationId, id);
      const { success, message } = replyDetail;

      if (success) {
        setIsSendingMsg(false);
        setMessageContent("");
        setSelectedMentionData([]);
        mention.reset();
        handleRemoveFiles();
        Chat.reFetchThreadDetails(id);
      } else {
        setIsSendingMsg(false);
        showSwal(common.errors.SEND_MESSAGE_ERROR);
      }
    } catch (err) {
      setIsSendingMsg(false);
      showSwal(common.errors.SEND_MESSAGE_ERROR);
    }
  };

  const handleDeleteMsg = async (msgId) => {
    try {
      const deleteMsg = await ApiService.message.deleteMessage(Chat.storedConversationId, msgId);

      const { success, conversation_action } = deleteMsg;
      if (!success) showSwal(common.errors.DELETE_MESSAGE_ERROR);
      WS.websocketInstance.sendMessage({ message: conversation_action, type: "user-actions-chat" });
      Chat.reFetchThreadDetails(id);
    } catch (err) {
      console.error(err);
    }
  };

  const groupedReaction = groupReactions(message_reactions);

  const userOnline = onlineUserData ? onlineUserData?.is_online : false;
  const lastLogin = userOnline ? "" : onlineUserData?.last_online ? getTimeAgo(onlineUserData?.last_online) : null;

  return (
    <div className='modal-container'>
      <div className='modal-dialog view-thread-modal-dialog view-thread-modal-post'>
        <button className='close-btn view-thread-close-btn' onClick={() => Chat.handleThreadViewVisible()}>
          ✕
        </button>
        <div className='view-thread-modal-header'>
          <h3>Thread</h3>
          <small>{allItems.length} {allItems.length === 1 ? "reply" : "replies"}</small>
        </div>

        <div className='modal-content view-thread-container'>
          <div className='view-thread-scroll-body'>
            <section className='view-thread-post' aria-label='Original message'>
              <div className='view-thread-post-header'>
                <img src={buildProfileImageSrc(user?.profile_photo)} alt='profile' className='view-thread-avatar' />
                <div className={`online-indicator-thread ${userOnline ? "online-user" : "offline-user"}`}>
                  <span className='tooltip'>
                    {`${userOnline ? "Online" : `${lastLogin !== null && lastLogin !== undefined ? lastLogin : "Offline"}`}`}
                  </span>
                </div>

                <div className='view-thread-author-block'>
                  <h4>{user?.display_name}</h4>
                  <small title={convertTo12HourFormat(createdAt)}>{getTimeAgo(createdAt)}</small>
                </div>
              </div>

              <div className='view-thread-post-content'>
                <div className='view-thread-user-chat'>
                  <span
                    ref={messageBodyRef}
                    className={`view-thread-message-body${!isPostExpanded ? " view-thread-message-body--clamped" : ""}`}
                  >
                    <MessageComponent message={content} is_deleted={is_deleted} />
                  </span>
                  {!is_deleted && isPostTruncatable ? (
                    <button
                      type='button'
                      className='view-thread-see-more-btn'
                      onClick={() => setIsPostExpanded((prev) => !prev)}
                    >
                      {isPostExpanded ? "See less" : "See more"}
                    </button>
                  ) : null}
                </div>

                {is_deleted ? (
                  ""
                ) : (
                  <div className='view-thread-user-images'>
                    {(() => {
                      const FINAL_RESOURCES_URL =
                        process.env.REACT_APP_CHAT_RESOURCES_URL;
                      const lightboxImages = message_attachment
                        .filter((attachment) =>
                          attachment?.file_type?.includes("image"),
                        )
                        .map((attachment) => ({
                          src: `${FINAL_RESOURCES_URL}/${attachment.attachment}`,
                          type: "image",
                        }));
                      let imageIndex = 0;

                      return message_attachment.map((attachment, index) => {
                        const isImage =
                          attachment?.file_type?.includes("image");
                        const currentImageIndex = isImage
                          ? imageIndex++
                          : 0;

                        return renderAttachment(
                          attachment.attachment,
                          attachment.file_type,
                          false,
                          index,
                          isImage ? lightboxImages : null,
                          currentImageIndex,
                        );
                      });
                    })()}
                  </div>
                )}
              </div>

              <div className='view-thread-root-reactions view-chat-reactions view-chat-reactions-receiver'>
                {Object.keys(groupedReaction).map((reaction, index) => (
                  <ReactionsIndicator
                    reaction={`${reaction}`}
                    reaction_details={groupedReaction[reaction]}
                    key={index}
                    isThread={true}
                  />
                ))}
              </div>
            </section>

            <section className='view-thread-replies-section' aria-label='Thread replies'>
              <div className='view-thread-replies-container'>
                {allItems.map((thread_reply, index) => {
                  const onlineUserList = Chat.onlineUsers;
                  const onlineData = onlineUserList.find((onlineUser) => onlineUser.user_id === thread_reply?.user?.id);
                  const userOnline = onlineData ? onlineData?.is_online : false;
                  const lastLogin = userOnline ? "" : `${getTimeAgo(onlineData?.last_online)}`;

                  return (
                    <div className='view-thread-reply' key={index}>
                      <div className='view-thread-reply-main'>
                        <div className='view-thread-reply-user-information'>
                          <img
                            src={buildProfileImageSrc(thread_reply?.user?.profile_photo)}
                            alt='profile'
                            className='view-thread-avatar view-thread-avatar-reply'
                          />
                          <div className={`online-indicator-thread ${userOnline ? "online-user" : "offline-user"}`}>
                            <span className='tooltip'>{`${userOnline ? "Online" : `${lastLogin}`}`}</span>
                          </div>

                          <div className='view-thread-author-block view-thread-reply-body'>
                            <h4>{thread_reply?.user?.display_name}</h4>
                            <small title={convertTo12HourFormat(thread_reply?.createdAt)}>
                              {getTimeAgo(thread_reply?.createdAt)}
                            </small>
                            {thread_reply?.is_deleted ? (
                              <span className='deleted-chat'>DELETED CHAT</span>
                            ) : (
                              <span className='view-thread-comment-text'>{formatBoldText(thread_reply?.content)}</span>
                            )}
                          </div>
                        </div>

                        {thread_reply?.is_deleted ? (
                          ""
                        ) : (
                          <div className='view-thread-reply-images'>
                            {(() => {
                              const FINAL_RESOURCES_URL =
                                process.env.REACT_APP_CHAT_RESOURCES_URL;
                              const lightboxImages =
                                thread_reply.message_attachment
                                  .filter((attachment) =>
                                    attachment?.file_type?.includes("image"),
                                  )
                                  .map((attachment) => ({
                                    src: `${FINAL_RESOURCES_URL}/${attachment.attachment}`,
                                    type: "image",
                                  }));
                              let imageIndex = 0;

                              return thread_reply.message_attachment.map(
                                (attachment, index) => {
                                  const isImage =
                                    attachment?.file_type?.includes("image");
                                  const currentImageIndex = isImage
                                    ? imageIndex++
                                    : 0;

                                  return renderAttachment(
                                    attachment.attachment,
                                    attachment.file_type,
                                    true,
                                    index,
                                    isImage ? lightboxImages : null,
                                    currentImageIndex,
                                  );
                                },
                              );
                            })()}
                          </div>
                        )}
                      </div>

                      {userId === thread_reply?.user?.id && !thread_reply?.is_deleted ? (
                        <button
                          type='button'
                          className='view-thread-reply-options'
                          aria-label='Delete thread reply'
                          onClick={() => handleDeleteMsg(thread_reply.id)}
                        >
                          <i className='fa fa-trash-o fa-2x' aria-hidden='true'></i>
                        </button>
                      ) : (
                        ""
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          </div>

          <section className='view-thread-inputs' aria-label='Reply composer'>
            <div className='view-thread-reply-attachments-container'>
              {filePreview.map((_filePreview, index) => renderUserChatAttachment(_filePreview, index))}
            </div>

            <div className='mention-composer'>
              {mention.pickerOpen && (
                <MentionPicker
                  query={mention.mentionSession.query}
                  options={mention.mentionOptions}
                  activeIndex={mention.activeIndex}
                  onActiveIndexChange={mention.setActiveIndex}
                  onSelect={mention.handleSelect}
                  listId='mention-picker-thread'
                />
              )}
              <div className='view-thread-reply-inputs-container'>
              <MentionComposerField
                textareaRef={textAreaRef}
                variant='thread'
                className='message__input view-thread-reply-textarea js-message--input msg-tool'
                placeholder={
                  !Chat.isPermittedToChat
                    ? "You are restricted from chatting in this conversation"
                    : "Write a reply..."
                }
                disabled={!Chat.isPermittedToChat}
                value={messageContent}
                autoComplete='off'
                aria-autocomplete='list'
                aria-expanded={mention.pickerOpen}
                aria-controls={mention.pickerOpen ? "mention-picker-thread" : undefined}
                aria-activedescendant={
                  mention.pickerOpen ? `mention-picker-thread-option-${mention.activeIndex}` : undefined
                }
                onChange={(e) => handleMessageInput(e)}
                onKeyDown={(e) => {
                  if (mention.handleKeyDown(e)) return;
                  if (e.key === "Enter" && e.shiftKey && Chat.isPermittedToChat) {
                    e.preventDefault();
                    setMessageContent((prevContent) => prevContent + "\n");
                  } else if (e.key === "Enter" && Chat.isPermittedToChat) {
                    e.preventDefault();
                    handleSendMessage();
                  }
                }}
                onKeyUp={mention.handleCaretMove}
                onClick={mention.handleCaretMove}
                onBlur={mention.handleBlur}
                onFocus={mention.handleFocus}
                onPaste={handleMessageInputPaste}
              />

              <div className='thread-reply-actions'>
                <input
                  type='file'
                  id='thread-fileInput'
                  className='message__file-input'
                  onChange={handleFileChange}
                  multiple
                  ref={fileInputRef}
                />

                <button
                  className='message__btn-file js-file-upload--btn msg-tool'
                  onClick={() => document.getElementById("thread-fileInput").click()}
                  aria-label='Attach file to thread reply'
                  disabled={!Chat.isPermittedToChat}
                >
                  <i className='fa fa-paperclip message__btn-file-icon'></i>
                </button>

                <button
                  className='message__btn-send js-send-message--btn msg-tool'
                  data-receiver=''
                  data-cid=''
                  aria-label='Send thread reply'
                  disabled={!Chat.isPermittedToChat || isSendingMsg}
                  onClick={() => {
                    if (Chat.isPermittedToChat) {
                      handleSendMessage();
                    }
                  }}
                >
                  {isSendingMsg ? (
                    <img src={loader2} className='loader-msg' alt='sending' />
                  ) : (
                    <i className='fa fa-paper-plane message__btn-send-icon'></i>
                  )}
                </button>
              </div>
            </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

export default ViewThreadModal;
